import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Edit, Trash2, Search } from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { AddSupplySheet } from "./AddSupplySheet";
import { EditSupplySheet } from "./EditSupplySheet";
import { formatCurrency } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";
import { Package } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useAppAuth } from "@/contexts/AuthContext";

export function SuppliesList() {
  const { hasPermission } = useAppAuth();
  const canWrite = hasPermission("clients:write");
  const [isAddSupplyOpen, setIsAddSupplyOpen] = useState(false);
  const [editingSupply, setEditingSupply] = useState<Id<"supplies"> | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [supplyToDelete, setSupplyToDelete] = useState<{ id: Id<"supplies">; itemName: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const supplies = useQuery(api.supplies.list) || [];
  const deleteSupply = useMutation(api.supplies.remove);

  const handleDelete = async () => {
    if (!supplyToDelete) return;
    setIsDeleting(true);
    try {
      await deleteSupply({ id: supplyToDelete.id });
      toast.success("Supply deleted successfully");
      setSupplyToDelete(null);
    } catch (error) {
      toast.error("Failed to delete supply");
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter supplies based on search query
  const filteredSupplies = Array.isArray(supplies)
    ? supplies.filter(
        (item) =>
          item.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.category?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
          (item.brand?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
          (item.vendor?.toLowerCase() || "").includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Master Supply List</h1>
          <p className="mt-1 text-gray-500">Manage your organization's supply inventory</p>
        </div>
        {canWrite && (
          <Button className="gap-2" onClick={() => setIsAddSupplyOpen(true)}>
            <Plus className="h-4 w-4" /> Add Supply
          </Button>
        )}
      </div>

      <Card className="w-full p-6">
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search supplies..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="w-full overflow-auto">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Item Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Cost Per Unit</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSupplies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState
                      icon={Package}
                      title="No supplies yet"
                      description={
                        searchQuery
                          ? "No supplies found matching your search."
                          : "Add your first supply to get started."
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredSupplies.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell className="font-medium">{item.itemName}</TableCell>
                    <TableCell>{item.category || "-"}</TableCell>
                    <TableCell>{item.brand || "-"}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>{formatCurrency(item.costPerUnit)}</TableCell>
                    <TableCell>{item.vendor || "-"}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingSupply(item._id)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => setSupplyToDelete({ id: item._id, itemName: item.itemName })}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <AddSupplySheet
        isOpen={isAddSupplyOpen}
        onClose={() => setIsAddSupplyOpen(false)}
        onSupplyAdded={() => {
          setIsAddSupplyOpen(false);
        }}
      />

      <EditSupplySheet
        isOpen={editingSupply !== null}
        onClose={() => setEditingSupply(null)}
        supplyId={editingSupply}
        onSupplyUpdated={() => {
          setEditingSupply(null);
        }}
      />

      <AlertDialog
        open={!!supplyToDelete}
        onOpenChange={(open) => !open && setSupplyToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete {supplyToDelete?.itemName} and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
